import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Spinner } from '@economic/taco';
import { refreshCurrentLegacyFrame, useCurrentUser } from '@economic/react-app';
import { t } from '~/app/shared/components/l10n/l10n';
import useQuery from '~/app/shared/hooks/useQuery';
import { PaymentAccountDto } from '~/types/internal-api';
import { ProgressDots } from '~/app/shared/components/ui/progressDots/ProgressDots';
import Form from '~/app/shared/components/Form/Form';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { CustomerPaymentPricePlan, CustomerPaymentSetupModel } from '~/types';
import { CompanyResponse } from '~/app/pages/accounting/payment-cards/type';
import track from '~/mixpanel-service';
import AwaitingPensoPay from './components/steps/AwaitingPensoPay';
import ConnectedToPensoPay from './components/steps/ConnectedToPensoPay';
import PaymentAndAccountingSettings from './components/steps/PaymentAndAccountingSettings';
import ContactInformation from './components/steps/ContactInformation';
import CompanyInformation from './components/steps/CompanyInformation';
import { OnboardingError, OnboardingStep, areaName, actionName } from './types';
import DialogFooter from './components/DialogFooter';
import SetUp from './components/steps/SetUp';
import OnboardingFailed from './components/steps/OnboardingFailed';
import PricePlan from './components/steps/PricePlan';

const initialOnboardingData: CustomerPaymentSetupModel = {
    consent: false,
    pricePlan: CustomerPaymentPricePlan.CardPlan,
    companyCvr: '',
    companyName: '',
    businessType: { physicalProduct: false, digitalSoftware: false, services: false },
    physicalProductDetail: {
        one: {
            name: '',
            averagePrice: null,
        },
        two: {
            name: '',
            averagePrice: null,
        },
        three: {
            name: '',
            averagePrice: null,
        },
        estimatedMaxTransactionAmount: null,
        markets: [],
    },
    companyDescription: '',
    fullName: '',
    email: '',
    telephone: '',
    paymentAccount: null,
    receivableAccount: null,
    bankIban: '',
    daybook: -999,
    feeAccount: null,
};

export const CustomerPaymentsOnboardingDialog = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [step, setStep] = useState(OnboardingStep.SetUp);
    const [onboardingError, setOnboardingError] = useState<OnboardingError>();
    const [pensoPayRequestLoading, setPensoPayRequestLoading] = useState<boolean>(false);

    const open = location.hash === '#onboardPaymentLink';

    const { loading: loadingCompanyData, data: companyData } = useQuery<CompanyResponse>('/company/getCurrentCompany', null);

    const { data: dataBankAccounts, refetch: refetchBankAccounts } = useQuery<PaymentAccountDto[]>(
        '/paymentAccounts/accounts',
        null
    );

    const user = useCurrentUser();

    const { firstName, lastName, email } = user;
    const companyCvr = companyData?.model?.cvr;
    const companyName = companyData?.model?.name;

    const maxVisualSteps = 6;

    const onboardingData = {
        ...initialOnboardingData,
        fullName: `${firstName} ${lastName}`,
        companyName,
        email,
        companyCvr,
    };

    const hide = () => {
        navigate(location.pathname);

        if (step === OnboardingStep.ConnectedToPensoPay) {
            refreshCurrentLegacyFrame();
        }
    };

    const handleSubmit = values => {
        setPensoPayRequestLoading(true);

        internalApiClient
            .post('customer-payments/create-setup', values)
            .then(() => {
                setStep(OnboardingStep.ConnectedToPensoPay);
            })
            .catch(({ response: { data } }) => {
                setStep(OnboardingStep.OnboardingFailed);
                setOnboardingError(data);
            })
            .finally(() => {
                setPensoPayRequestLoading(false);
            });
    };

    useEffect(() => {
        track(areaName, actionName, { page: step });
    }, [step]);

    // Spinner while requests are loading
    if (loadingCompanyData || !dataBankAccounts) {
        return (
            <Dialog open={open} onClose={hide}>
                <Dialog.Content className="flex flex-col items-center" aria-label={t`TOAST_LOADING`}>
                    <Spinner delay={0} />
                </Dialog.Content>
            </Dialog>
        );
    }

    // Show loading screen when sending onboarding data to BE/pensopay
    if (pensoPayRequestLoading) {
        return <AwaitingPensoPay />;
    }

    // Start onboarding flow
    return (
        <Dialog open={open} onClose={hide} closeOnEscape={false} size="xs">
            <Dialog.Content aria-label="customer-payments">
                <div className="w-full flex justify-center mb-6">
                    <ProgressDots
                        stepsCount={maxVisualSteps}
                        currentStep={step > maxVisualSteps ? 6 : step}
                        withCompleteTicks={false}
                    />
                </div>

                <Form initialData={onboardingData} onSubmit={handleSubmit} validateOnBlur={false} skipNotifications>
                    {() => (
                        <>
                            {step === OnboardingStep.SetUp && <SetUp nextStep={() => setStep(OnboardingStep.PricePlan)} />}
                            {step === OnboardingStep.PricePlan && (
                                <PricePlan
                                    prevStep={() => setStep(OnboardingStep.SetUp)}
                                    nextStep={() => setStep(OnboardingStep.CompanyInformation)}
                                />
                            )}
                            {step === OnboardingStep.CompanyInformation && (
                                <CompanyInformation
                                    prevStep={() => setStep(OnboardingStep.PricePlan)}
                                    nextStep={() => setStep(OnboardingStep.YourContactInformation)}
                                    companyData={companyData}
                                />
                            )}
                            {step === OnboardingStep.YourContactInformation && (
                                <ContactInformation
                                    prevStep={() => setStep(OnboardingStep.CompanyInformation)}
                                    nextStep={() => setStep(OnboardingStep.PaymentAndAccountsSettings)}
                                />
                            )}
                            {step === OnboardingStep.PaymentAndAccountsSettings && (
                                <PaymentAndAccountingSettings
                                    prevStep={() => setStep(OnboardingStep.YourContactInformation)}
                                    refetchBankAccounts={refetchBankAccounts}
                                />
                            )}
                            {step === OnboardingStep.ConnectedToPensoPay && <ConnectedToPensoPay hide={hide} />}
                            {step === OnboardingStep.OnboardingFailed && onboardingError && (
                                <OnboardingFailed onboardingError={onboardingError} />
                            )}
                        </>
                    )}
                </Form>

                <DialogFooter />
            </Dialog.Content>
        </Dialog>
    );
};
